<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('horse.title') }}</span>
          <div class="header-actions-wrapper">
            <div class="header-actions">
              <el-button 
                @click="handleRefresh"
                :icon="Refresh"
              >
                {{ $t('common.refresh') }}
              </el-button>
              <el-button type="primary" @click="goToAdd">
                <el-icon><Plus /></el-icon>
                {{ $t('horse.newHorse') }}
              </el-button>
            </div>
            <el-button-group class="view-toggle">
              <el-button 
                :type="viewMode === 'list' ? 'primary' : ''"
                @click="viewMode = 'list'"
              >
                <el-icon><List /></el-icon>
              </el-button>
              <el-button 
                :type="viewMode === 'card' ? 'primary' : ''"
                @click="viewMode = 'card'"
              >
                <el-icon><Grid /></el-icon>
              </el-button>
            </el-button-group>
          </div>
        </div>
      </template>

      <!-- List View -->
      <el-table 
        v-if="viewMode === 'list'" 
        :data="horseStore.horses" 
        v-loading="loading" 
        stripe
      >
        <el-table-column type="index" width="50" />
        <el-table-column prop="avatar" :label="$t('horse.avatar')" width="100">
          <template #default="{ row }">
            <ImagePreview 
              v-if="row.avatar" 
              :src="row.avatar" 
              width="50px" 
              height="50px"
              :enable-preview="true"
            />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="$t('horse.name')" />
        <el-table-column prop="breed" :label="$t('horse.breed')" />
        <el-table-column prop="color" :label="$t('horse.color')" />
        <el-table-column prop="age" :label="$t('horse.age')" width="80" />
        <el-table-column prop="gender" :label="$t('horse.gender')" width="80">
          <template #default="{ row }">
            <el-tag :type="row.gender ? 'primary' : 'info'">
              {{ row.gender ? '♂' : '♀' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="birthDate" :label="$t('horse.birthDate')" width="120">
          <template #default="{ row }">
            {{ row.birthDate ? new Date(row.birthDate).toLocaleDateString() : '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.actions')" width="200" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" link @click="viewDetail(row)">
              {{ $t('common.detail') }}
            </el-button>
            <el-button type="primary" link @click="goToEdit(row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button type="danger" link @click="handleDelete(row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- Card View -->
      <div v-else class="horse-cards" v-loading="loading">
        <div v-if="horseStore.horses.length === 0" class="empty-state">
          <el-empty :description="$t('horse.noHorses')" />
        </div>
        <div v-else class="cards-grid">
          <div 
            v-for="horse in horseStore.horses" 
            :key="horse.id" 
            class="horse-card"
          >
            <div class="card-image">
              <ImagePreview 
                v-if="horse.avatar" 
                :src="horse.avatar" 
                width="100%" 
                height="200px"
                :enable-preview="true"
                fit="cover"
              />
              <div v-else class="image-placeholder">
                <el-icon class="placeholder-icon"><Picture /></el-icon>
              </div>
            </div>
            <div class="card-content">
              <div class="card-header-info">
                <h3 class="horse-name">{{ horse.name }}</h3>
                <el-tag :type="horse.gender ? 'primary' : 'info'" size="small">
                  {{ horse.gender ? '♂' : '♀' }}
                </el-tag>
              </div>
              <div class="card-info">
                <div class="info-item">
                  <el-icon><Flag /></el-icon>
                  <span>{{ horse.breed || '-' }}</span>
                </div>
                <div class="info-item">
                  <el-icon><MagicStick /></el-icon>
                  <span>{{ horse.color || '-' }}</span>
                </div>
                <div class="info-item">
                  <el-icon><Timer /></el-icon>
                  <span>{{ $t('horse.age') }}: {{ horse.age || '-' }}</span>
                </div>
                <div class="info-item" v-if="horse.birthDate">
                  <el-icon><Calendar /></el-icon>
                  <span>{{ new Date(horse.birthDate).toLocaleDateString() }}</span>
                </div>
              </div>
              <div class="card-actions">
                <el-button type="primary" size="small" @click="viewDetail(horse)">
                  <el-icon><View /></el-icon>
                  {{ $t('common.detail') }}
                </el-button>
                <el-button type="warning" size="small" @click="goToEdit(horse)">
                  <el-icon><Edit /></el-icon>
                  {{ $t('common.edit') }}
                </el-button>
                <el-button type="danger" size="small" @click="handleDelete(horse)">
                  <el-icon><Delete /></el-icon>
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useHorseStore } from '@/stores/horse'
import ImagePreview from '@/components/ImagePreview.vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { List, Grid, Plus, Picture, Flag, MagicStick, Timer, Calendar, View, Edit, Delete, Refresh } from '@element-plus/icons-vue'
import { imageCacheUtil } from '@/utils/image-cache'

const router = useRouter()
const { t } = useI18n()
const horseStore = useHorseStore()

const loading = ref(false)
const viewMode = ref(window.innerWidth < 768 ? 'card' : 'list')

const fetchData = async (forceRefresh = false) => {
  loading.value = true
  await horseStore.fetchHorses(forceRefresh)
  loading.value = false
}

// Load saved view mode preference
onMounted(() => {
  const savedViewMode = localStorage.getItem('horseViewMode')
  if (savedViewMode) {
    viewMode.value = savedViewMode
  }
})

// Save view mode preference
watch(() => viewMode.value, (newValue) => {
  localStorage.setItem('horseViewMode', newValue)
})

// Handle manual refresh - clear cache and reload
const handleRefresh = async () => {
  console.log('[Horse List] Manual refresh...')
  
  // Clear cached image data
  imageCacheUtil.clearAllCachedImages()
  
  // Reload data from API
  await fetchData()
  
  ElMessage.success('Refreshed successfully')
}

const goToAdd = () => {
  router.push('/horse/add')
}

const goToEdit = (row) => {
  router.push(`/horse/edit/${row.id}`)
}

const viewDetail = (row) => {
  router.push(`/horse/detail/${row.id}`)
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(t('common.confirmDelete'), t('common.tip'), { type: 'warning' })
    await horseStore.removeHorse(row.id)
    ElMessage.success(t('common.success'))
    fetchData()
  } catch (error) {
    if (error !== 'cancel') console.error(error)
  }
}

onMounted(fetchData)
</script>

<style scoped lang="scss">
.page-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  :deep(.el-card) {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    :deep(.el-card__header) {
      flex-shrink: 0;
    }

    :deep(.el-card__body) {
      flex: 1;
      overflow: auto;
      padding: 0;
    }
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-actions-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }

  .header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .view-toggle {
    .el-button {
      padding: 8px 12px;
    }
  }
}

.horse-cards {
  padding: 20px;

  .empty-state {
    padding: 60px 20px;
    text-align: center;
  }

  .cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }

  .horse-card {
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
    }

    .card-image {
      position: relative;
      width: 100%;
      height: 200px;
      background-color: #f5f7fa;
    
      .image-placeholder {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);
    
        .placeholder-icon {
          font-size: 48px;
          color: #c0c4cc;
        }
      }
    }

    .card-content {
      padding: 16px;

      .card-header-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;

        .horse-name {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #303133;
        }
      }

      .card-info {
        margin-bottom: 16px;

        .info-item {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          font-size: 14px;
          color: #606266;

          .el-icon {
            color: #909399;
            font-size: 16px;
          }

          &:last-child {
            margin-bottom: 0;
          }
        }
      }

      .card-actions {
        display: flex;
        gap: 8px;
        padding-top: 12px;
        border-top: 1px solid #f0f2f5;

        .el-button {
          flex: 1;
        }
      }
    }
  }
}

// Mobile responsive styles
@media (max-width: 768px) {
  .page-container {
    :deep(.el-card) {
      :deep(.el-card__header) {
        padding: 12px 16px;

        .card-header {
          flex-direction: column;
          gap: 12px;
          align-items: flex-start;

          .header-actions-wrapper {
            display: flex;
            flex-direction: column;
            gap: 10px;
            align-items: center;
            width: 100%;
          }

          .header-actions {
            width: 100%;
            justify-content: center;
          }

          .view-toggle {
            .el-button {
              padding: 8px 12px;
            }
          }
        }
      }

      :deep(.el-card__body) {
        padding: 8px;
      }
    }
  }

  .horse-cards {
    padding: 12px;

    .cards-grid {
      grid-template-columns: 1fr;
      gap: 12px;
    }

    .horse-card {
      .card-image {
        height: 180px;
      }

      .card-content {
        padding: 12px;

        .card-header-info {
          .horse-name {
            font-size: 16px;
          }
        }

        .card-info {
          .info-item {
            font-size: 13px;
          }
        }

        .card-actions {
          flex-wrap: wrap;

          .el-button {
            flex: 1 1 calc(50% - 4px);
            min-width: calc(50% - 4px);

            &:last-child {
              flex: 1 1 100%;
            }
          }
        }
      }
    }
  }

  // Mobile table optimizations
  :deep(.el-table) {
    font-size: 13px;

    :deep(.el-table__cell) {
      padding: 8px 4px;
    }

    :deep(.el-button--link) {
      padding: 4px 8px;
      font-size: 12px;
    }
  }
}

// Small mobile devices
@media (max-width: 480px) {
  .page-container {
    :deep(.el-card) {
      :deep(.el-card__header) {
        padding: 10px 12px;

        .card-header {
          span {
            font-size: 16px;
          }

          .header-actions-wrapper {
            display: flex;
            flex-direction: column;
            gap: 8px;
            align-items: center;
          }

          .header-actions {
            gap: 6px;
            width: 100%;

            .el-button {
              padding: 8px 12px;
              font-size: 13px;
              min-width: 100px;
            }
          }

          .view-toggle {
            .el-button {
              padding: 8px 10px;
              min-width: 40px;
            }
          }
        }
      }
    }
  }

  :deep(.el-table) {
    :deep(.el-table__cell) {
      padding: 6px 2px;
    }
  }

  .horse-cards {
    padding: 8px;

    .horse-card {
      .card-image {
        height: 160px;
      }

      .card-content {
        padding: 10px;

        .card-header-info {
          .horse-name {
            font-size: 15px;
          }
        }

        .card-info {
          .info-item {
            font-size: 12px;
            margin-bottom: 6px;
          }
        }
      }
    }
  }
}
</style>
